import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

/* ------------------------------------------------------------------ */
/* Type system: this is how ML vs Automation vs Web get differentiated */
/* ------------------------------------------------------------------ */
const TYPES = {
  ai: { label: 'AI / ML', glow: '132, 0, 255', text: 'text-violet-200', chip: 'bg-violet-500/15 border-violet-400/30' },
  automation: { label: 'Automation & Bots', glow: '16, 185, 129', text: 'text-emerald-200', chip: 'bg-emerald-500/15 border-emerald-400/30' },
  web: { label: 'Web', glow: '56, 189, 248', text: 'text-sky-200', chip: 'bg-sky-500/15 border-sky-400/30' },
};

const cardData = [
  {
    label: 'Experience',
    experiences: [
      {
        company: 'Capgemini Invent',
        position: 'Fullstack GenAI Developer',
        period: 'Aug 2026 – Present',
        achievements: [
          'Design and ship full-stack GenAI applications, connecting LLM-powered backends to polished, production-ready frontends.',
          'Build automations and AI agents that streamline business workflows, cutting manual effort and turning unstructured data into actionable outputs.',
          'Collaborate with cross-functional teams to translate ambiguous client needs into reliable, scalable AI-driven solutions.',
        ],
      },
      {
        company: 'Stashed SNKRS',
        position: 'Software Developer',
        period: 'Jun 2025 – Oct 2025',
        achievements: [
          'Maintained and improved backend systems powering bots, automation tools, and internal services.',
          'Developed an interactive web platform integrating backend APIs, improving user experience and showcasing frontend–backend communication.',
          'Implemented AI-driven automation to optimize workflows and enhance operational efficiency, solving ambiguous technical challenges in a fast-paced environment.',
        ],
      },
      {
        company: 'Delos AI Inc.',
        position: 'Software Developer',
        period: 'Dec 2024 – Jan 2025',
        achievements: [
          'Engineered serverless distributed systems using AWS to automate document processing pipelines and payment operations, ensuring reliability, fault tolerance, and scalability.',
          'Utilized OCR and AI to transform large unstructured datasets into structured outputs, enabling downstream analytics and automated decision-making.',
        ],
      },
    ],
  },
  {
    label: 'Projects',
    projects: [
      {
        type: 'ai',
        title: 'LSTM Piano Roll Generator',
        desc: 'A PyTorch LSTM that predicts chords from classical music, using a novel approach that seeds the hidden state with composer information. Benchmarked the composer-aware model against a standard LSTM on a MIDI dataset to measure gains in style-aware music generation.',
        stack: ['PyTorch', 'LSTM', 'MIDI'],
        img: '/imgs/beet.png',
      },
      {
        type: 'ai',
        title: 'Reinforcement Learning Blackjack Agent',
        desc: 'A self-taught RL agent that plays Blackjack via Q-learning. Trained in a simulated environment, it reached a 44% win-rate — matching optimal book play — with zero hand-coded strategy.',
        stack: ['Q-learning', 'RL', 'Python'],
        images: ['/imgs/eval_results_pie.png', '/imgs/episode_rewards.png', '/imgs/eval_results_actions.png'],
      },
      {
        type: 'ai',
        title: 'VGG16 Tinder Agent',
        desc: 'A bot that trains a VGG16 CNN in TensorFlow to detect hair color, then swipes automatically on Tinder based on a user’s preference — computer vision driving a real-world action loop.',
        stack: ['TensorFlow', 'VGG16', 'CV'],
        video: '/imgs/tin.mp4',
      },
      {
        type: 'automation',
        title: 'Autonomous Shopping Agent',
        desc: 'A high-throughput checkout bot using proxies, threading, and web scraping to purchase limited drops. Ships with a GUI to set item, instances, and a max-cost safety cap. Runs across Supreme, Stussy, and Palace.',
        stack: ['Python', 'Threading', 'Proxies', 'Scraping'],
        video: '/imgs/stus.mp4',
      },
      {
        type: 'web',
        title: 'Personal Portfolio Website',
        desc: 'A bespoke personal portfolio built for a client — a fast, responsive single-page site with smooth animations, custom sections, and a clean, modern layout tailored to their personal brand.',
        stack: ['React', 'Tailwind', 'Framer Motion'],
        carousel: true,
        images: ['/imgs/mmm1.png', '/imgs/mmm2.png', '/imgs/mmm3.png'],
      },
    ],
  },
  {
    label: 'Education',
    education: [
      { institution: 'Vrije Universiteit Amsterdam', period: '2023 – Jul 2026', degree: 'BSc Artificial Intelligence' },
      { institution: 'Sungkyunkwan University, South Korea', period: 'Sep 2025 – Dec 2025', degree: 'Software Engineering Exchange Semester' },
      { institution: 'American School of Warsaw', period: '2018 – 2022', degree: 'IB Diploma' },
    ],
  },
];

/* ------------------------------------------------------------------ */
/* Type badge                                                          */
/* ------------------------------------------------------------------ */
function TypeBadge({ type }) {
  const t = TYPES[type];
  if (!t) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${t.chip} ${t.text}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: `rgb(${t.glow})` }} />
      {t.label}
    </span>
  );
}

/* ------------------------------------------------------------------ */
/* Image carousel (for web / multi-screenshot projects)               */
/* ------------------------------------------------------------------ */
function Carousel({ images, title }) {
  const [i, setI] = useState(0);
  const go = (dir) => setI((prev) => (prev + dir + images.length) % images.length);
  return (
    <div className="relative w-full h-full group/carousel bg-black/40">
      <AnimatePresence initial={false} mode="wait">
        <motion.img
          key={i}
          src={images[i]}
          alt={`${title} — ${i + 1}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.35 }}
          className="w-full h-full object-cover object-top"
        />
      </AnimatePresence>

      {images.length > 1 && (
        <>
          <button
            onClick={() => go(-1)}
            aria-label="Previous image"
            className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-black/70"
          >
            ‹
          </button>
          <button
            onClick={() => go(1)}
            aria-label="Next image"
            className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover/carousel:opacity-100 transition-opacity hover:bg-black/70"
          >
            ›
          </button>
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
            {images.map((_, d) => (
              <button
                key={d}
                onClick={() => setI(d)}
                aria-label={`Go to image ${d + 1}`}
                className={`h-1.5 rounded-full transition-all ${d === i ? 'w-5 bg-white' : 'w-1.5 bg-white/40 hover:bg-white/70'}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Project card                                                        */
/* ------------------------------------------------------------------ */
function ProjectCard({ p }) {
  const glow = TYPES[p.type]?.glow || '132, 0, 255';
  return (
    <motion.article
      layout
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{ type: 'spring', stiffness: 260, damping: 26 }}
      whileHover={{ y: -6 }}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-sm"
      style={{ '--glow': glow }}
    >
      <div
        className="pointer-events-none absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"
        style={{ boxShadow: `inset 0 0 0 1px rgba(${glow}, 0.5), 0 20px 60px -20px rgba(${glow}, 0.5)` }}
      />
      {/* media */}
      <div className="relative w-full aspect-video overflow-hidden bg-black/40">
        {p.img && <img src={p.img} alt={p.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />}
        {p.images && p.carousel && <Carousel images={p.images} title={p.title} />}
        {p.images && !p.carousel && (
          <div className="grid grid-cols-3 gap-1 h-full">
            {p.images.map((src, i) => (
              <img key={i} src={src} alt={`${p.title} ${i + 1}`} className="w-full h-full object-cover" />
            ))}
          </div>
        )}
        {p.video && (
          <video className="w-full h-full object-cover" controls playsInline preload="metadata">
            <source src={p.video} type="video/mp4" />
          </video>
        )}
      </div>
      {/* body */}
      <div className="flex flex-col flex-1 p-5">
        <div className="mb-3"><TypeBadge type={p.type} /></div>
        <h3 className="text-lg font-semibold text-white">{p.title}</h3>
        <p className="mt-2 text-sm text-white/60 leading-relaxed flex-1">{p.desc}</p>
        {p.stack && (
          <div className="mt-4 flex flex-wrap gap-1.5">
            {p.stack.map((s) => (
              <span key={s} className="text-[11px] px-2 py-0.5 rounded-md bg-white/5 border border-white/10 text-white/60">{s}</span>
            ))}
          </div>
        )}
      </div>
    </motion.article>
  );
}

/* ------------------------------------------------------------------ */
/* Filter pills                                                        */
/* ------------------------------------------------------------------ */
function FilterPills({ types, active, setActive }) {
  const options = ['all', ...types];
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const isActive = active === opt;
        const label = opt === 'all' ? 'All' : TYPES[opt].label;
        return (
          <button
            key={opt}
            onClick={() => setActive(opt)}
            className={`relative px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              isActive ? 'text-white' : 'text-white/50 hover:text-white/80'
            }`}
          >
            {isActive && (
              <motion.span
                layoutId="filterPill"
                className="absolute inset-0 rounded-full bg-white/10 border border-white/20"
                transition={{ type: 'spring', stiffness: 380, damping: 30 }}
              />
            )}
            <span className="relative z-10">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Section heading                                                     */
/* ------------------------------------------------------------------ */
function SectionHeading({ kicker, title }) {
  return (
    <div className="mb-8">
      <div className="text-xs uppercase tracking-[0.3em] text-violet-300/70">{kicker}</div>
      <h2 className="mt-1 text-3xl md:text-4xl font-bold text-white">{title}</h2>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */
const MagicBento = () => {
  const projects = cardData.find((c) => c.label === 'Projects')?.projects || [];
  const experiences = cardData.find((c) => c.label === 'Experience')?.experiences || [];
  const education = cardData.find((c) => c.label === 'Education')?.education || [];

  // derive which type filters actually exist so it self-extends when you add a web project
  const presentTypes = Object.keys(TYPES).filter((t) => projects.some((p) => p.type === t));
  const [active, setActive] = useState('all');
  const visible = active === 'all' ? projects : projects.filter((p) => p.type === active);

  return (
    <div className="w-full max-w-5xl mx-auto px-2 space-y-28 py-6">
      {/* Projects */}
      <section id="projects" className="scroll-mt-24">
        <SectionHeading kicker="Selected Work" title="Projects" />
        <div className="mb-8"><FilterPills types={presentTypes} active={active} setActive={setActive} /></div>
        <motion.div layout className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <AnimatePresence mode="popLayout">
            {visible.map((p) => <ProjectCard key={p.title} p={p} />)}
          </AnimatePresence>
        </motion.div>
      </section>

      {/* Experience */}
      <section id="experience" className="scroll-mt-24">
        <SectionHeading kicker="Where I've shipped" title="Experience" />
        <div className="relative border-l border-white/10 ml-2 space-y-8">
          {experiences.map((exp, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5 }}
              className="relative pl-8"
            >
              <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-violet-500 ring-4 ring-violet-500/20" />
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <div className="text-lg font-semibold text-white">{exp.position}</div>
                  <div className="text-sm text-violet-300">{exp.company}</div>
                </div>
                <div className="text-xs text-white/40">{exp.period}</div>
              </div>
              <ul className="mt-3 space-y-1.5 text-sm text-white/60">
                {exp.achievements.map((a, ai) => (
                  <li key={ai} className="flex gap-2"><span className="text-violet-400 mt-1">›</span><span>{a}</span></li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Education */}
      <section id="education" className="scroll-mt-24">
        <SectionHeading kicker="Background" title="Education" />
        <div className="grid gap-4 md:grid-cols-3">
          {education.map((edu, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 backdrop-blur-sm"
            >
              <div className="text-xs text-white/40">{edu.period}</div>
              <div className="mt-1 font-semibold text-white leading-snug">{edu.institution}</div>
              <div className="mt-1 text-sm text-violet-300">{edu.degree}</div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Connect + light side-project note */}
      <section id="connect" className="scroll-mt-24">
        <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-violet-500/10 to-transparent p-8 md:p-10 text-center backdrop-blur-sm">
          <h2 className="text-2xl md:text-3xl font-bold text-white">Let's build something</h2>
          <p className="mt-3 text-white/60 max-w-xl mx-auto">
            I'm a developer by day — but always open to interesting AI, automation, or side projects.
            If you've got something worth making, I'd love to hear about it.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <a href="mailto:czarnecki.janmarceli@gmail.com" className="px-5 py-2.5 rounded-full bg-white text-black font-medium hover:bg-white/90 transition-colors">Get in touch</a>
            <a href="https://www.linkedin.com/in/jan-czarnecki-181733251/" target="_blank" rel="noreferrer" className="px-5 py-2.5 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">LinkedIn</a>
            <a href="/imgs/janczarnecki-cv.pdf" download className="px-5 py-2.5 rounded-full border border-white/20 text-white hover:bg-white/10 transition-colors">Download CV</a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MagicBento;
